/**
 * Every refresh-token family on the instance (CLAUDE.md 11.7, 12.2).
 *
 * Revoking here kills the *refresh* token. The holder's current access token stays
 * valid until it expires — 15 minutes by default — because it is a signed bearer token
 * and nothing consults the database to accept it. That is stated in the UI rather than
 * left for an administrator to discover, since "sign them out now" and "sign them out
 * within fifteen minutes" are materially different promises during an incident.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PAGE_SIZE, useAdminSessions, useRevokeSession } from '@/api/admin'
import { ApiError } from '@/api/client'
import {
  Pagination,
  Pill,
  TableScroller,
  Td,
  Th,
  formatDateTime,
} from '@/components/admin/AdminShell'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'

export function AdminSessions() {
  const { t } = useTranslation(['admin', 'common'])
  const { toast } = useToast()

  const [includeInactive, setIncludeInactive] = useState(false)
  const [offset, setOffset] = useState(0)

  const sessions = useAdminSessions({ active_only: !includeInactive, offset })
  const revoke = useRevokeSession()

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin.sessions.title')}</h2>

      <div className="flex items-center gap-2">
        <input
          id="include-inactive"
          type="checkbox"
          checked={includeInactive}
          onChange={(event) => {
            setIncludeInactive(event.target.checked)
            setOffset(0)
          }}
        />
        <label htmlFor="include-inactive" className="text-sm">
          {t('admin.sessions.show_revoked')}
        </label>
      </div>

      {sessions.data && sessions.data.items.length === 0 ? (
        <p className="rounded-lg border border-border p-6 text-center text-sm text-text-muted">
          {t('admin.sessions.empty')}
        </p>
      ) : (
        <>
          <TableScroller>
            <thead>
              <tr>
                <Th>{t('common.name', { ns: 'common' })}</Th>
                <Th>{t('admin.sessions.ip')}</Th>
                <Th>{t('admin.sessions.user_agent')}</Th>
                <Th>{t('admin.sessions.last_used')}</Th>
                <Th className="text-end">{t('common.actions', { ns: 'common' })}</Th>
              </tr>
            </thead>
            <tbody>
              {sessions.data?.items.map((row) => (
                <tr key={row.id}>
                  <Td>
                    <div className="font-medium">{row.display_name}</div>
                    <div className="text-xs text-text-muted">{row.username}</div>
                  </Td>
                  {/* An IP address is an identifier, never translated or localised. */}
                  <Td className="font-mono text-xs">{row.ip ?? '—'}</Td>
                  <Td className="max-w-xs truncate text-xs text-text-muted">
                    {row.user_agent ?? '—'}
                  </Td>
                  <Td className="whitespace-nowrap text-text-muted">
                    {formatDateTime(row.last_used_at ?? row.created_at)}
                  </Td>
                  <Td>
                    <div className="flex justify-end">
                      {row.revoked ? (
                        <Pill tone="muted">{t('admin.sessions.revoked')}</Pill>
                      ) : (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() =>
                            revoke.mutate(row.id, {
                              onSuccess: () =>
                                toast({
                                  title: t('common.saved', { ns: 'common' }),
                                  variant: 'success',
                                }),
                              onError: (error) =>
                                toast({
                                  title: t('common.error', { ns: 'common' }),
                                  description:
                                    error instanceof ApiError ? error.localised : undefined,
                                  variant: 'error',
                                }),
                            })
                          }
                        >
                          {t('admin.sessions.revoke')}
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableScroller>

          <Pagination
            total={sessions.data?.total ?? 0}
            limit={PAGE_SIZE}
            offset={offset}
            onChange={setOffset}
          />
        </>
      )}
    </div>
  )
}
